import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, BookOpen, CalendarCheck, CaretDown, ChartLine, Check, CheckCircle, ClipboardText, Clock,
  ClockCounterClockwise, CurrencyEur, EnvelopeSimple, FlowArrow, ForkKnife, Gift, GoogleLogo, GraduationCap,
  IdentificationCard, Megaphone, Microphone, PhoneCall, QrCode, Robot, Rocket, ShieldCheck, Sparkle, Storefront,
  Sun, Ticket, Users, VideoCamera, Waveform, WhatsappLogo,
} from "@phosphor-icons/react";
import api from "../lib/api";

const HERO_IMG = "https://images.pexels.com/photos/14803087/pexels-photo-14803087.jpeg";
const CHEF_IMG = "https://images.pexels.com/photos/36430088/pexels-photo-36430088.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const POS_IMG = "https://images.unsplash.com/photo-1658282687011-2a6a3d55eb0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx0YWJsZXQlMjByZXN0YXVyYW50JTIwcG9pbnQlMjBvZiUyMHNhbGV8ZW58MHx8fHwxNzg4MDg3NTQwfDA&ixlib=rb-4.1.0&q=85";

const OVERLINE = "text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold";
const inputCls = "w-full h-12 px-4 rounded-lg bg-zinc-900 border border-zinc-800 text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-[#2ce0cb] focus:ring-1 focus:ring-[#2ce0cb] transition-colors";
const cardCls = "bg-zinc-900 border border-zinc-800 rounded-2xl p-8 min-w-0 transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-zinc-600";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } } };
const stagger = { show: { transition: { staggerChildren: 0.12 } } };

const PLANS = [
  { key: "base", name: "Base", monthly: 49, yearly: 490,
    tagline: "Per partire con prenotazioni e menu digitale",
    features: ["Prenotazioni illimitate", "Sale e tavoli con panoramica live",
      "Moduli web di prenotazione", "Menu digitale con QR personalizzabile",
      "CRM clienti con rubrica completa"] },
  { key: "pro", name: "Pro", monthly: 89, yearly: 890, highlight: true,
    tagline: "Il marketing che riempie il locale",
    features: ["Tutto il piano Base", "Campagne email e WhatsApp con statistiche reali",
      "Automazioni e workflow visuali", "Coupon, gift card e sondaggi post-visita",
      "Tag e segmenti avanzati sul CRM"] },
  { key: "premium", name: "Premium", monthly: 149, yearly: 1490,
    tagline: "Operatività completa, zero pensieri",
    features: ["Tutto il piano Pro", "Coda walk-in e scanner QR in sala",
      "Centralino con numero virtuale e registro chiamate", "Wallet SMS con pacchetti dedicati",
      "Onboarding assistito e supporto prioritario", "Personalizzazioni su misura"] },
];

const LEAD_EMPTY = { nome: "", ristorante: "", email: "", telefono: "", messaggio: "" };

const VENUE_TYPES = ["Ristoranti", "Pizzerie", "Trattorie", "Osterie", "Sushi", "Bistrò",
  "Hamburgerie", "Pub", "Birrerie", "Cocktail Bar", "Enoteche", "Agriturismi"];

const FAQS = [
  ["Quanto costa iniziare?", "Niente: i primi 14 giorni sono gratuiti e non serve la carta di credito. I prezzi sono pubblici qui sopra — niente preventivi nascosti o sorprese."],
  ["Devo installare un programma o un'app?", "No. Dishenda funziona dal browser, su PC, tablet e smartphone. Lo usi in sala, in cucina o da casa."],
  ["Serve un corso di formazione?", "No: le interfacce sono pensate per chi lavora in sala, non per i tecnici. E se vuoi, un tutor dedicato ti segue gratuitamente nella configurazione iniziale con video call 1-a-1."],
  ["I dati dei miei clienti sono al sicuro?", "Sì: i consensi marketing GDPR sono integrati in ogni modulo pubblico e i dati restano tuoi. Niente rivendite, niente condivisioni."],
  ["Posso disdire quando voglio?", "Sì, nessun vincolo: l'abbonamento è mensile o annuale e lo gestisci in autonomia dall'area Impostazioni."],
  ["Funziona anche per pizzerie e altri locali?", "Sì: ristoranti, pizzerie, trattorie, osterie, sushi, pub, birrerie, cocktail bar, enoteche, agriturismi — se hai tavoli e clienti, Dishenda fa per te."],
];

function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-800 rounded-xl bg-zinc-900 overflow-hidden transition-colors hover:border-zinc-600" data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="text-sm font-bold text-zinc-100">{q}</span>
        <CaretDown size={16} weight="bold" className={`text-[#2ce0cb] shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm text-zinc-400 leading-relaxed" data-testid={`${testid}-answer`}>{a}</p>}
    </div>
  );
}

function LeadMagnet() {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const download = () => {
    const a = document.createElement("a");
    a.href = "/vetrina/checklist-10-modi.pdf";
    a.download = "checklist-10-modi-per-riempire-il-tuo-locale.pdf";
    a.click();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/leads", { nome, email, fonte: "checklist-pdf",
        messaggio: "Ha scaricato la checklist '10 modi per riempire il tuo locale'" });
      setDone(true);
      download();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invio non riuscito, riprova");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 sm:p-8" data-testid="leadmagnet-card">
      {done ? (
        <div className="text-center py-8" data-testid="lm-success">
          <CheckCircle size={52} weight="fill" className="text-emerald-400 mx-auto" />
          <h3 className="font-display text-2xl font-bold mt-4 text-zinc-50">Checklist in arrivo!</h3>
          <p className="text-zinc-400 text-sm mt-2">Il download è partito. Buona lettura — e buon pienone.</p>
          <button onClick={download} data-testid="lm-download"
            className="mt-6 h-11 px-6 rounded-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm font-bold hover:bg-zinc-800 transition-colors">
            Scarica di nuovo il PDF
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="lm-form">
          <input required value={nome} onChange={(e) => setNome(e.target.value)}
            placeholder="Il tuo nome *" className={inputCls} data-testid="lm-nome" />
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="La tua email *" className={inputCls} data-testid="lm-email" />
          <button disabled={sending} data-testid="lm-submit"
            className="w-full h-12 rounded-full bg-[#2ce0cb] text-zinc-950 font-bold text-sm hover:bg-[#6ef0e0] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50">
            {sending ? "Preparazione..." : "Scarica la checklist gratuita"}
          </button>
          <p className="text-[10px] text-zinc-600 text-center">Lasciando l'email accetti di ricevere la checklist e qualche consiglio utile. Nessuno spam.</p>
        </form>
      )}
    </div>
  );
}

function LeadForm() {
  const [form, setForm] = useState(LEAD_EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/leads", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invio non riuscito, riprova");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="text-center py-14" data-testid="v-lead-success">
        <CheckCircle size={56} weight="fill" className="text-emerald-400 mx-auto" />
        <h3 className="font-display text-2xl font-bold mt-4 text-zinc-50">Richiesta inviata!</h3>
        <p className="text-zinc-400 text-sm mt-2">Ti ricontattiamo entro 24 ore lavorative.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="v-lead-form">
      <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
        placeholder="Il tuo nome e cognome *" className={inputCls} data-testid="v-lead-nome" />
      <input value={form.ristorante} onChange={(e) => setForm({ ...form, ristorante: e.target.value })}
        placeholder="Nome del ristorante" className={inputCls} data-testid="v-lead-ristorante" />
      <div className="grid sm:grid-cols-2 gap-3">
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email *" className={inputCls} data-testid="v-lead-email" />
        <input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
          placeholder="Telefono" className={inputCls} data-testid="v-lead-telefono" />
      </div>
      <textarea value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
        placeholder="Raccontaci il tuo locale (facoltativo)" rows={3}
        className={`${inputCls} h-auto py-3 resize-none`} data-testid="v-lead-messaggio" />
      <button disabled={sending} data-testid="v-lead-submit"
        className="w-full h-12 rounded-full bg-[#2ce0cb] text-zinc-950 font-bold text-sm hover:bg-[#6ef0e0] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50">
        {sending ? "Invio in corso..." : "Richiedi informazioni"}
      </button>
      <p className="text-[10px] text-zinc-600 text-center">Inviando accetti di essere ricontattato da Dishenda. Nessuno spam, promesso.</p>
    </form>
  );
}

function FlowNode({ kind, text, accent }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`rounded-lg border px-3 py-2 text-xs font-semibold flex-1 ${accent ? "border-[#2ce0cb]/40 bg-[#2ce0cb]/10 text-[#6ef0e0]" : "border-zinc-800 bg-zinc-950 text-zinc-300"}`}>
        <span className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">{kind}</span>
        {text}
      </div>
    </div>
  );
}

export default function Vetrina() {
  useEffect(() => {
    document.title = "Dishenda — Il gestionale che fa tornare i tuoi clienti";
    return () => { document.title = "Dishenda"; };
  }, []);
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 text-left overflow-x-clip" data-testid="vetrina-page"
      style={{ backgroundImage: "url(/vetrina/bg-linee.png)", backgroundRepeat: "repeat-y",
               backgroundPosition: "right top", backgroundAttachment: "fixed" }}>

      {/* ---------- NAV ---------- */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800/50" data-testid="vetrina-nav">
        <div className="max-w-7xl mx-auto px-5 h-20 flex items-center justify-between">
          <img src="/vetrina/logo-dishenda.png" alt="Dishenda" className="h-16" data-testid="nav-logo" />
          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#funzionalita" className="hover:text-zinc-100 transition-colors" data-testid="nav-funzionalita">Funzionalità</a>
            <a href="#assistente" className="hover:text-zinc-100 transition-colors" data-testid="nav-assistente">Assistente AI</a>
            <a href="#per-chi" className="hover:text-zinc-100 transition-colors" data-testid="nav-per-chi">Per chi</a>
            <a href="#schermate" className="hover:text-zinc-100 transition-colors" data-testid="nav-schermate">Schermate</a>
            <a href="#prezzi" className="hover:text-zinc-100 transition-colors" data-testid="nav-prezzi">Prezzi</a>
            <a href="#faq" className="hover:text-zinc-100 transition-colors" data-testid="nav-faq">FAQ</a>
            <a href="/login" className="hover:text-zinc-100 transition-colors" data-testid="nav-login">Accedi</a>
          </div>
          <a href="#contatti" data-testid="nav-cta"
            className="h-9 px-5 rounded-full bg-[#2ce0cb] text-zinc-950 text-xs font-bold flex items-center hover:bg-[#6ef0e0] transition-colors">
            Richiedi info
          </a>
        </div>
      </nav>

      {/* ---------- HERO ---------- */}
      <header className="relative overflow-hidden" data-testid="vetrina-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Sala di un ristorante moderno in penombra" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-zinc-950/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/70 to-zinc-950/20" />
        </div>
        <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-white/[0.05] blur-3xl pointer-events-none" aria-hidden="true" />
        <img src="/vetrina/logo-glyph.png" alt="" aria-hidden="true"
          className="absolute -right-16 top-1/2 -translate-y-1/2 w-[440px] opacity-[0.11] pointer-events-none select-none" />
        <div className="relative max-w-7xl mx-auto px-5 pt-40 pb-28 sm:pt-48 sm:pb-36">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className={OVERLINE} data-testid="hero-overline">Gestionale per ristoranti · Tutto in uno</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
            className="font-display text-5xl md:text-6xl font-extrabold tracking-tighter leading-[1.05] mt-5 max-w-2xl"
            data-testid="hero-title">
            Il gestionale che fa <span className="text-[#2ce0cb]">tornare</span> i tuoi clienti
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-6 text-zinc-400 text-base sm:text-lg leading-relaxed max-w-xl" data-testid="hero-subtitle">
            Prenotazioni, sala, CRM, coupon, gift card, sondaggi, menu digitale e marketing
            automatico. Un'unica app pensata per i ristoranti, non per i tecnici.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-9 flex flex-wrap gap-3">
            <a href="#contatti" data-testid="hero-cta-prova"
              className="h-12 px-7 rounded-full bg-[#2ce0cb] text-zinc-950 text-sm font-bold flex items-center gap-2 hover:bg-[#6ef0e0] active:scale-[0.98] transition-[background-color,transform]">
              Prova gratis 14 giorni <ArrowRight size={16} weight="bold" />
            </a>
            <a href="#schermate" data-testid="hero-cta-schermate"
              className="h-12 px-7 rounded-full bg-zinc-900/80 backdrop-blur text-zinc-50 text-sm font-semibold border border-zinc-700 flex items-center hover:bg-zinc-800 transition-colors">
              Vedi le schermate
            </a>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
            className="mt-6 text-xs text-zinc-500">Nessuna carta richiesta · Attivazione in 5 minuti · Assistenza in italiano</motion.p>
        </div>
      </header>

      {/* ---------- TRUST STRIP ---------- */}
      <section className="border-y border-zinc-900 bg-zinc-950" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-5 py-7 grid grid-cols-2 md:grid-cols-4 gap-5">
          {[[ShieldCheck, "Prezzi pubblici e trasparenti"], [Rocket, "Attivo in 5 minuti, senza corsi"],
            [Sparkle, "Assistente IA integrato"], [ForkKnife, "Fatto da ristoratori, per ristoratori"]].map(([Icon, txt]) => (
            <div key={txt} className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-[#2ce0cb]/10 border border-[#2ce0cb]/30 flex items-center justify-center shrink-0">
                <Icon size={17} weight="duotone" className="text-[#2ce0cb]" />
              </span>
              <span className="text-xs font-semibold text-zinc-300 leading-snug">{txt}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- BENTO FUNZIONALITÀ ---------- */}
      <section id="funzionalita" className="py-24 sm:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-5">
          <p className={OVERLINE}>Funzionalità</p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4 max-w-2xl">
            Tutto il locale, in un colpo d'occhio
          </h2>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="grid md:grid-cols-12 gap-6 mt-14" data-testid="features-grid">

            <motion.div variants={fadeUp} className={`md:col-span-8 ${cardCls} grid sm:grid-cols-2 gap-6 items-center`} data-testid="feature-crm">
              <div>
                <Users size={28} weight="duotone" className="text-[#2ce0cb]" />
                <h3 className="font-display text-2xl font-bold tracking-tight mt-4">CRM e segmenti intelligenti</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mt-3">
                  Ogni cliente ha una scheda con storico visite, spesa e preferenze. Tag automatici
                  e segmenti pronti per il marketing: i clienti giusti, al momento giusto.
                </p>
                <ul className="mt-4 space-y-2 text-xs text-zinc-500">
                  <li className="flex items-center gap-2"><Check size={13} weight="bold" className="text-[#2ce0cb]" /> Tag automatici da prenotazioni e coupon</li>
                  <li className="flex items-center gap-2"><Check size={13} weight="bold" className="text-[#2ce0cb]" /> Segmenti riusabili nelle campagne</li>
                  <li className="flex items-center gap-2"><Check size={13} weight="bold" className="text-[#2ce0cb]" /> Consensi marketing GDPR integrati</li>
                </ul>
              </div>
              <img src={CHEF_IMG} alt="Chef che impiatta con cura" className="rounded-xl border border-zinc-800 object-cover h-56 w-full" />
            </motion.div>

            <motion.div variants={fadeUp} className={`md:col-span-4 ${cardCls}`} data-testid="feature-prenotazioni">
              <CalendarCheck size={28} weight="duotone" className="text-[#2ce0cb]" />
              <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Prenotazioni e sale live</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3">
                Richieste da confermare, tavoli e stati in tempo reale. La sala lavora da sola.
              </p>
              <div className="mt-5 space-y-2">
                {[["19:30", "Rossi · 4 pers.", "Confermata", "text-emerald-400"],
                  ["20:00", "Bianchi · 2 pers.", "Da confermare", "text-[#2ce0cb]"],
                  ["21:00", "Verdi · 6 pers.", "In sala", "text-sky-400"]].map(([ora, chi, stato, cls]) => (
                  <div key={chi} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-xs">
                    <span className="font-mono text-zinc-500">{ora}</span>
                    <span className="font-semibold text-zinc-300">{chi}</span>
                    <span className={`font-bold ${cls}`}>{stato}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className={`md:col-span-4 md:row-span-2 ${cardCls}`} data-testid="feature-automazioni">
              <FlowArrow size={28} weight="duotone" className="text-[#2ce0cb]" />
              <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Automazioni visuali</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3">
                Costruisci flussi con attese, condizioni e azioni: email e WhatsApp partono da soli,
                mentre tu sei in sala.
              </p>
              <div className="mt-6 space-y-3">
                <FlowNode kind="Trigger" text="Cliente esce dalla sala" accent />
                <div className="w-px h-4 bg-zinc-700 ml-6" />
                <FlowNode kind="Attesa" text="2 ore dopo" />
                <div className="w-px h-4 bg-zinc-700 ml-6" />
                <FlowNode kind="Azione" text="Invia sondaggio + coupon" accent />
                <div className="w-px h-4 bg-zinc-700 ml-6" />
                <FlowNode kind="Risultato" text="Il cliente torna" />
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className={`md:col-span-8 ${cardCls}`} data-testid="feature-marketing">
              <Megaphone size={28} weight="duotone" className="text-[#2ce0cb]" />
              <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Coupon, gift card e sondaggi</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3 max-w-lg">
                Coupon scaricabili con QR e PDF brandizzato, gift card con codice automatico,
                sondaggi post-visita che alimentano il CRM. Campagne email e WhatsApp con
                aperture e click reali, non stime.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-zinc-500">
                <span className="flex items-center gap-2 text-xs font-semibold"><Ticket size={18} weight="duotone" className="text-[#2ce0cb]" /> Coupon</span>
                <span className="flex items-center gap-2 text-xs font-semibold"><Gift size={18} weight="duotone" className="text-[#2ce0cb]" /> Gift card</span>
                <span className="flex items-center gap-2 text-xs font-semibold"><ClipboardText size={18} weight="duotone" className="text-[#2ce0cb]" /> Sondaggi</span>
                <span className="flex items-center gap-2 text-xs font-semibold"><WhatsappLogo size={18} weight="duotone" className="text-[#2ce0cb]" /> WhatsApp</span>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className={`md:col-span-4 ${cardCls}`} data-testid="feature-menu">
              <QrCode size={28} weight="duotone" className="text-[#2ce0cb]" />
              <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Menu digitale con QR</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3">
                Menù multipli, categorie, allergeni UE e foto. Tema personalizzabile — anche con
                l'assistente IA — e QR pronto da stampare.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className={`md:col-span-4 ${cardCls}`} data-testid="feature-coda">
              <Clock size={28} weight="duotone" className="text-[#2ce0cb]" />
              <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Coda walk-in</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mt-3">
                Il cliente si mette in coda dal telefono, vede posizione e attesa stimata, e riceve
                l'avviso quando il tavolo è pronto.
              </p>
              <img src={POS_IMG} alt="Tablet con gestionale in un ristorante" className="rounded-xl border border-zinc-800 object-cover h-32 w-full mt-5" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ---------- ASSISTENTE AI ---------- */}
      <section id="assistente" className="py-24 sm:py-32 border-t border-zinc-900 relative overflow-hidden" data-testid="assistant-section">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
            <motion.p variants={fadeUp} className={OVERLINE}>Assistente AI integrato</motion.p>
            <motion.h2 variants={fadeUp} className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4">
              Il gestionale che si comanda a voce
            </motion.h2>
            <motion.p variants={fadeUp} className="text-zinc-400 text-base sm:text-lg leading-relaxed mt-6 max-w-xl">
              Dì "Ehi Dishenda" e basta: l'assistente capisce, agisce e risponde a voce.
              Non un chatbot che spiega — un collega che esegue, mentre tu sei in sala.
            </motion.p>
            <motion.ul variants={stagger} className="mt-10 space-y-6">
              {[
                [Microphone, "Comandi vocali hands-free", "Wake word \"Ehi Dishenda\", dettatura in italiano e risposte lette ad alta voce. Mani libere durante il servizio."],
                [Sparkle, "Esegue, non consiglia", "\"Metti Rossi arrivato\", \"Crea una prenotazione per domani alle 20\", \"Quante richieste da confermare?\": lo fa davvero, in tempo reale."],
                [Sun, "Briefing proattivo del mattino", "Ogni giorno analizza prenotazioni, coperti e clienti inattivi e ti propone l'azione che recupera fatturato — pronta da eseguire con un tap."],
                [GoogleLogo, "Prenota con Google", "Il pulsante \"Prenota un tavolo\" su Maps e Ricerca collegato al tuo modulo: i clienti arrivano da Google senza commissioni per coperto."],
              ].map(([Icon, t, d]) => (
                <motion.li key={t} variants={fadeUp} className="flex gap-4">
                  <span className="w-10 h-10 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                    <Icon size={20} weight="duotone" className="text-[#2ce0cb]" />
                  </span>
                  <div>
                    <p className="font-display font-bold text-zinc-100">{t}</p>
                    <p className="text-sm text-zinc-400 leading-relaxed mt-1">{d}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}
            className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-5 space-y-3 backdrop-blur-xl"
            data-testid="ai-chat-mock">
            <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
              <Robot size={18} weight="bold" className="text-[#2ce0cb]" />
              <p className="font-display font-bold text-sm text-zinc-100">Assistente Dishenda</p>
              <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                IN ASCOLTO
              </span>
            </div>
            <div className="flex justify-end">
              <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-sm bg-sky-600 text-white text-sm">
                Ehi Dishenda, metti Rossi arrivato
              </div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-zinc-800 text-zinc-200 text-sm space-y-2">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  <Check size={10} weight="bold" /> Aggiorno lo stato
                </span>
                <p>Fatto: Rossi segnato come arrivato (IN), tavolo 12, 4 coperti.</p>
              </div>
            </div>
            <div className="flex justify-end">
              <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-sm bg-sky-600 text-white text-sm">
                Quante richieste ci sono da confermare?
              </div>
            </div>
            <div className="flex justify-start">
              <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-zinc-800 text-zinc-200 text-sm space-y-2">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  <Check size={10} weight="bold" /> Richieste da confermare
                </span>
                <p>Ce ne sono 3: Bianchi alle 20:00 (4 coperti), Verdi alle 20:30 (2), Greco alle 21:00 (6). Le confermo tutte?</p>
              </div>
            </div>
            <div className="rounded-xl border border-[#2ce0cb]/30 bg-[#2ce0cb]/5 p-3.5">
              <p className="text-[10px] uppercase tracking-widest font-bold text-[#2ce0cb]">Briefing del mattino</p>
              <p className="text-sm text-zinc-300 mt-1.5 leading-relaxed">
                Giovedì hai 12 coperti contro i 34 del solito giovedì. Con una campagna ai
                48 clienti inattivi da 30+ giorni bastano 6 coperti per pareggiare: la preparo?
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- CENTRALINO ---------- */}
      <section id="centralino" className="py-24 sm:py-32 border-t border-zinc-900" data-testid="centralino-section">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className={OVERLINE}>Centralino integrato</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4">
              Ogni telefonata diventa un'opportunità
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mt-5 max-w-lg">
              Il numero del locale vive dentro Dishenda: chiami i clienti con un tap,
              riconosci chi ti sta cercando e ogni conversazione resta agganciata alla scheda
              cliente. Niente più prenotazioni segnate su fogli volanti.
            </p>
            <div className="mt-8 space-y-4">
              {[[PhoneCall, "Click-to-call dalla scheda cliente", "Chiami direttamente dal gestionale: un tap e parti, senza comporre numeri."],
                [IdentificationCard, "Chiamante riconosciuto", "Se il numero è in rubrica vedi subito nome, storico visite e preferenze."],
                [ClockCounterClockwise, "Registro chiamate completo", "Entrate, uscite e perse: durata, esito e note sempre a portata di mano."],
                [Waveform, "Orari, inoltro e registrazione", "Numero virtuale del locale, inoltro fuori orario e registrazione opzionale."]].map(([Icon, t, s]) => (
                <div key={t} className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                    <Icon size={20} weight="duotone" className="text-[#2ce0cb]" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-zinc-100">{t}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{s}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={cardCls} data-testid="centralino-mock">
            <div className="flex items-center justify-between">
              <p className={OVERLINE}>Registro chiamate</p>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#2ce0cb]/10 text-[#2ce0cb]">Oggi</span>
            </div>
            <div className="mt-5 space-y-2">
              {[["Giulia Bianchi", "+39 333 123 4567", "Prenotazione per 4 · sabato", "text-emerald-400", "2:14"],
                ["Numero sconosciuto", "+39 348 987 6543", "Nuovo contatto aggiunto al CRM", "text-[#2ce0cb]", "1:02"],
                ["Marco Rossi", "+39 320 456 7890", "Spostata alle 21:00", "text-sky-400", "0:47"],
                ["Chiamata persa", "+39 339 111 2233", "Richiamata automatica in lista", "text-amber-400", "—"]].map(([chi, num, esito, cls, dur]) => (
                <div key={num} className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-xs">
                  <PhoneCall size={15} weight="duotone" className="text-zinc-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-zinc-200 truncate">{chi} <span className="font-mono font-normal text-zinc-500">{num}</span></p>
                    <p className={`font-bold ${cls} mt-0.5`}>{esito}</p>
                  </div>
                  <span className="font-mono text-zinc-600 shrink-0">{dur}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-600 mt-4">Ogni chiamata alimenta il CRM: chi chiama diventa un cliente da far tornare.</p>
          </div>
        </div>
      </section>

      {/* ---------- ONBOARDING CON TUTOR ---------- */}
      <section id="onboarding" className="py-24 sm:py-32 border-t border-zinc-900" data-testid="onboarding-section">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className={OVERLINE}>Onboarding guidato</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4">
              Non sarai mai solo
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mt-5 max-w-lg">
              Niente corsi di formazione costosi: un tutor dedicato ti segue nella configurazione
              iniziale finché il locale non lavora da solo. Gratis, per sempre incluso.
            </p>
            <div className="mt-8 space-y-4">
              {[[GraduationCap, "Tutor dedicato al tuo fianco", "Una persona vera che conosce il tuo locale e i tuoi obiettivi."],
                [VideoCamera, "Video call 1-a-1", "Configuriamo insieme prenotazioni, menu, coupon e automazioni."],
                [BookOpen, "Manuale passo-passo", "Guide semplici sempre disponibili, scritte per ristoratori."]].map(([Icon, t, s]) => (
                <div key={t} className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                    <Icon size={20} weight="duotone" className="text-[#2ce0cb]" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-zinc-100">{t}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{s}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={cardCls} data-testid="onboarding-steps">
            <p className={OVERLINE}>Come funziona</p>
            <div className="mt-6 space-y-0">
              {[["1", "Attivi la prova gratuita", "2 minuti, senza carta di credito"],
                ["2", "Configuri con il tutor", "Una video call e il locale è pronto"],
                ["3", "Il locale lavora da solo", "Prenotazioni, marketing e clienti che tornano"]].map(([n, t, s], i, arr) => (
                <div key={n} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <span className="w-9 h-9 rounded-full bg-[#2ce0cb] text-zinc-950 font-display font-extrabold text-sm flex items-center justify-center shrink-0">{n}</span>
                    {i < arr.length - 1 && <span className="w-px flex-1 bg-zinc-800 my-1" />}
                  </div>
                  <div className="pb-8">
                    <p className="text-sm font-bold text-zinc-100 pt-2">{t}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{s}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- SCHERMATE ---------- */}
      <section id="schermate" className="py-24 sm:py-32 border-t border-zinc-900 relative overflow-hidden" data-testid="screenshots-section">
        <div className="absolute right-0 top-0 w-[480px] h-[480px] rounded-full bg-white/[0.05] blur-3xl pointer-events-none" aria-hidden="true" />
        <img src="/vetrina/logo-glyph.png" alt="" aria-hidden="true"
          className="absolute right-4 top-2 w-[360px] opacity-[0.13] pointer-events-none select-none" />
        <div className="max-w-7xl mx-auto px-5 relative">
          <p className={OVERLINE}>Dentro l'app</p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4 max-w-2xl">
            Veloce come la tua sala
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed mt-4 max-w-xl">
            Schermate reali di Dishenda: la lista prenotazioni operativa, le automazioni di
            sistema e il menu digitale che i clienti aprono dal QR sul tavolo.
          </p>
          <div className="relative mt-16 md:pb-28" data-testid="screenshots-collage">
            <motion.img initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              src="/vetrina/screen-prenotazioni.jpg" alt="Schermata prenotazioni di Dishenda"
              className="w-full md:w-[68%] rounded-xl border border-zinc-800 shadow-2xl shadow-black/60"
              data-testid="shot-prenotazioni" />
            <motion.img initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}
              src="/vetrina/screen-automazioni.jpg" alt="Schermata automazioni di Dishenda"
              className="mt-6 md:mt-0 md:absolute md:right-0 md:top-20 w-full md:w-[46%] rounded-xl border border-zinc-800 shadow-2xl shadow-black/60 md:rotate-1"
              data-testid="shot-automazioni" />
            <motion.img initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }}
              src="/vetrina/screen-menu-mobile.jpg" alt="Menu digitale Dishenda su smartphone"
              className="mt-6 md:mt-0 md:absolute md:left-[3%] md:-bottom-10 w-40 md:w-44 rounded-2xl border border-zinc-700 shadow-2xl shadow-black/60 -rotate-2 max-md:mx-auto max-md:block"
              data-testid="shot-menu" />
          </div>
        </div>
      </section>

      {/* ---------- PER CHI ---------- */}
      <section id="per-chi" className="py-24 sm:py-32 border-t border-zinc-900" data-testid="venchi-section">
        <div className="max-w-7xl mx-auto px-5">
          <p className={OVERLINE}>Per chi</p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4 max-w-2xl">
            Se hai tavoli e clienti, fa per te
          </h2>
          <p className="text-zinc-400 text-base mt-4 max-w-xl">
            Ogni tipo di locale ha i suoi ritmi: Dishenda si adatta al tuo, dal pranzo veloce alla cena gourmet.
          </p>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
            {VENUE_TYPES.map((v) => (
              <motion.div key={v} variants={fadeUp} data-testid={`venue-${v.toLowerCase().replace(/\s/g, "-")}`}
                className="bg-zinc-900 border border-zinc-800 rounded-xl px-5 py-4 flex items-center gap-3 transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-[#2ce0cb]/50">
                <Storefront size={20} weight="duotone" className="text-[#2ce0cb] shrink-0" />
                <span className="text-sm font-bold text-zinc-100">{v}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------- PERCHÉ DISHENDA ---------- */}
      <section className="py-24 sm:py-32 border-t border-zinc-900" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-5">
          <p className={OVERLINE}>Perché Dishenda</p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4 max-w-2xl">
            Diverso dai soliti gestionali
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {[[CurrencyEur, "Prezzi pubblici, niente preventivi", "Vedi subito quanto costa e cosa include ogni piano. Nessuna call commerciale obbligatoria per sapere il prezzo."],
              [Rocket, "Prova gratuita self-service", "14 giorni per testare tutto con i tuoi dati reali. Lo attivi da solo in 5 minuti, quando vuoi tu."],
              [Sparkle, "L'IA lavora per te", "Temi del menu, loghi trasparenti e immagini di sfondo generati con l'intelligenza artificiale. Il tuo locale sembra avere un'agenzia grafica."],
              [ShieldCheck, "Nessun corso di formazione", "Se sai usare WhatsApp, sai usare Dishenda. Le strategie di marketing sono già pre-caricate: le personalizzi e basta."]].map(([Icon, t, s]) => (
              <motion.div key={t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                className={cardCls} data-testid={`why-${t.slice(0, 12).toLowerCase().replace(/\s/g, "-")}`}>
                <Icon size={26} weight="duotone" className="text-[#2ce0cb]" />
                <h3 className="font-display text-xl font-bold tracking-tight mt-4">{t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mt-2">{s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- PREZZI ---------- */}
      <section id="prezzi" className="py-24 sm:py-32 border-t border-zinc-900" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-5">
          <p className={OVERLINE}>Prezzi</p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4">
            Un piano per ogni locale
          </h2>
          <p className="text-zinc-400 text-base mt-4 max-w-xl">14 giorni di prova gratuita su tutti i piani. Annuale = 2 mesi gratis.</p>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {PLANS.map((p) => (
              <motion.div key={p.key} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                className={`rounded-2xl p-8 bg-zinc-900 flex flex-col transition-[transform,border-color] duration-200 hover:-translate-y-1 ${p.highlight ? "border border-[#2ce0cb]" : "border border-zinc-800 hover:border-zinc-600"}`}
                data-testid={`pricing-card-${p.key}`}>
                {p.highlight && <p className="text-xs uppercase tracking-[0.2em] text-[#2ce0cb] font-bold mb-3">Il più scelto</p>}
                <h3 className="font-display text-2xl font-bold tracking-tight">{p.name}</h3>
                <p className="text-xs text-zinc-500 mt-1">{p.tagline}</p>
                <div className="mt-6 flex items-end gap-1.5">
                  <span className="font-display text-5xl font-extrabold tracking-tighter" data-testid={`price-${p.key}`}>€{p.monthly}</span>
                  <span className="text-zinc-500 text-sm mb-1.5">/mese</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1">oppure €{p.yearly}/anno</p>
                <ul className="mt-7 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <Check size={15} weight="bold" className="text-[#2ce0cb] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <a href="#contatti" data-testid={`pricing-cta-${p.key}`}
                  className={`mt-8 h-11 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-[background-color,transform] active:scale-[0.98] ${p.highlight ? "bg-[#2ce0cb] text-zinc-950 hover:bg-[#6ef0e0]" : "bg-zinc-950 border border-zinc-700 text-zinc-100 hover:bg-zinc-800"}`}>
                  Inizia con {p.name} <ArrowRight size={15} weight="bold" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="py-24 sm:py-32 border-t border-zinc-900" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-5">
          <p className={OVERLINE}>Domande frequenti</p>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4">
            Le risposte che cerchi
          </h2>
          <div className="mt-12 space-y-3">
            {FAQS.map(([q, a], i) => <FaqItem key={q} q={q} a={a} testid={`faq-item-${i}`} />)}
          </div>
        </div>
      </section>

      {/* ---------- LEAD MAGNET ---------- */}
      <section id="checklist" className="py-24 sm:py-32 border-t border-zinc-900" data-testid="leadmagnet-section">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className={OVERLINE}>Risorsa gratuita</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4">
              Checklist: 10 modi per riempire il tuo locale
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mt-5 max-w-lg">
              Le strategie che i locali pieni usano ogni giorno, condensate in una checklist
              da spuntare. Lascia nome ed email: il PDF parte subito.
            </p>
            <ul className="mt-7 space-y-2.5">
              {["Anti no-show con reminder automatici", "Coupon, gift card e recupero clienti dormienti",
                "Sondaggi post-visita e compleanni automatici", "Come misurare il marketing con dati reali"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-zinc-300">
                  <Check size={15} weight="bold" className="text-[#2ce0cb] mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <LeadMagnet />
        </div>
      </section>

      {/* ---------- FORM LEAD ---------- */}
      <section id="contatti" className="py-24 sm:py-32 border-t border-zinc-900 relative overflow-hidden" data-testid="contact-section">
        <img src="/vetrina/logo-glyph.png" alt="" aria-hidden="true"
          className="absolute -left-24 top-1/2 -translate-y-1/2 w-[380px] opacity-[0.08] pointer-events-none select-none" />
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-start relative">
          <div>
            <p className={OVERLINE}>Contatti</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter mt-4">Parla con noi</h2>
            <p className="text-zinc-400 text-base leading-relaxed mt-5 max-w-md">
              Raccontaci il tuo locale: ti mostriamo Dishenda sui tuoi dati e ti attiviamo
              la prova gratuita. Nessun impegno, nessuna carta.
            </p>
            <div className="mt-8 space-y-4">
              <a href="mailto:info@cavadaconsulting.it" className="flex items-center gap-3 text-sm text-zinc-300 hover:text-[#2ce0cb] transition-colors" data-testid="contact-email">
                <span className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                  <EnvelopeSimple size={18} weight="duotone" className="text-[#2ce0cb]" />
                </span>
                info@cavadaconsulting.it
              </a>
              <div className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                  <ChartLine size={18} weight="duotone" className="text-[#2ce0cb]" />
                </span>
                Demo gratuita sui tuoi dati · risposta entro 24 ore lavorative
              </div>
              <div className="flex items-center gap-3 text-sm text-zinc-300">
                <span className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                  <ForkKnife size={18} weight="duotone" className="text-[#2ce0cb]" />
                </span>
                Fatto da ristoratori, per ristoratori
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 sm:p-8">
            <LeadForm />
          </div>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="border-t border-zinc-900 pt-20 pb-10 overflow-hidden" data-testid="vetrina-footer">
        <div className="max-w-7xl mx-auto px-5 flex flex-wrap gap-8 items-center justify-between text-xs text-zinc-500">
          <div className="flex gap-6">
            <a href="#funzionalita" className="hover:text-zinc-300 transition-colors">Funzionalità</a>
            <a href="#prezzi" className="hover:text-zinc-300 transition-colors">Prezzi</a>
            <a href="#contatti" className="hover:text-zinc-300 transition-colors">Contatti</a>
            <a href="/privacy" className="hover:text-zinc-300 transition-colors" data-testid="footer-privacy">Privacy</a>
            <a href="/cookie-policy" className="hover:text-zinc-300 transition-colors" data-testid="footer-cookie">Cookie</a>
            <a href="/login" className="hover:text-zinc-300 transition-colors" data-testid="footer-login">Accedi</a>
          </div>
          <p>© 2026 Dishenda · dishenda.it</p>
        </div>
        <div className="font-display font-extrabold tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-[#0e3a36] via-[#11424a] to-[#0e2f52] text-[13vw] leading-none text-center select-none mt-14" aria-hidden="true">
          DISHENDA
        </div>
      </footer>
    </div>
  );
}
